/**
 * @fileoverview Service Worker Service - Main thread utilities for communicating
 * with the Service Worker to provide throttle-resistant executions.
 *
 * This client library handles Service Worker registration and provides the same API
 * using Service Worker messaging.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';

// Type definitions
interface MessageCallback {
  (...args: any[]): void;
}

interface PromiseHandlers {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
}

export interface ServiceWorkerMessage {
  type: 'ASYNC_INTERVAL' | 'ASYNC_TIMEOUT' | 'HEARTBEAT';
  id: string;
  command: 'start' | 'stop' | 'beep';
  delay?: number;
  context?: any;
}

interface ServiceWorkerResponse {
  type: 'ASYNC_INTERVAL_TICK' | 'ASYNC_TIMEOUT_TICK' | 'HEARTBEAT';
  id?: string;
  result?: any;
  error?: {
    message: string;
    stack: string;
  };
  context?: any;
}

interface AsyncOptions {
  callerName?: string;
  immediateInvoke?: boolean;
  context?: any;
}

const log = new Logger('BackgroundService');

@Injectable({
  providedIn: 'root'
})
export class BackgroundService {
  private serviceWorker: ServiceWorker | null = null;
  private pendingCallbacks: Map<string, MessageCallback | PromiseHandlers> = new Map();
  private messageQueue: ServiceWorkerMessage[] = [];
  private nextId = 1;
  private readonly HEARTBEAT_PULSE_DELAY = appConstants.TIME.SS_FIVE;

  /**
   * Registers the Service Worker and sets up message handling
   * @param swPath - Path to the service worker file
   */
  init(swPath = '/sw.js'): void {
    if (this.serviceWorker) {
      return;
    }

    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Workers are not supported in this browser');
      }

      navigator.serviceWorker.register(swPath).then(async registration => {
        await navigator.serviceWorker.ready;

        this.serviceWorker = registration.active || registration.waiting || registration.installing;
        // Set up message listener
        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
          this.handleMessage(event);
        });

        // Trigger heartbeat to Service Worker
        this.sendHeartbeat();

        // Process any queued messages
        this.processMessageQueue();

        log.debug('Service Worker registered successfully');
      });
    } catch (error) {
      throw new Error(`Failed to register Service Worker: ${(error as Error).message}`);
    }
  }

  /**
   * Generates a unique ID for tracking operations
   * @returns Unique identifier string
   */
  private generateId(): string {
    return `client-${this.nextId++}`;
  }

  /**
   * Sends a message to the Service Worker
   * @param message - Message to send
   */
  private sendMessage(message: ServiceWorkerMessage): void {
    if (!this.serviceWorker) {
      // Queue message if service worker isn't ready
      this.messageQueue.push(message);
      return;
    }

    this.serviceWorker.postMessage(message);
  }

  /**
   * Processes queued messages after Service Worker registration
   */
  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.sendMessage(message);
      }
    }
  }

  /**
   * Handles messages from the Service Worker
   * @param event - Message event from Service Worker
   */
  private handleMessage(event: MessageEvent): void {
    const data = event.data as ServiceWorkerResponse;
    const { type, id, context } = data;

    switch (type) {
      case 'ASYNC_INTERVAL_TICK':
        if (id && this.pendingCallbacks.has(id)) {
          const callback = this.pendingCallbacks.get(id) as MessageCallback;
          if (callback && typeof callback === 'function') {
            log.debug('Executing background setInterval for: ', id, `at: ${new Date().toString()} `);
            callback();
          }
        }
        break;

      case 'ASYNC_TIMEOUT_TICK':
        if (id && this.pendingCallbacks.has(id)) {
          const callback = this.pendingCallbacks.get(id) as MessageCallback;
          if (callback && typeof callback === 'function') {
            log.debug('Executing background setTimeOut for: ', id, `at: ${new Date().toString()} `);
            callback(context);
          }
          this.pendingCallbacks.delete(id);
        }
        break;

      case 'HEARTBEAT':
        this.sendHeartbeat();
        break;
    }
  }
  /**
   * Creates an interval that runs in the Service Worker
   * @param callback - Function to execute at each interval
   * @param delay - Delay between executions in milliseconds
   * @param options - Configuration options
   * @returns Cleanup function to stop the interval
   */
  setAsyncInterval(callback: MessageCallback, delay: number, options: AsyncOptions = {}): () => void {
    if (!this.serviceWorker) {
      // Fallback to window.setInterval
      log.debug('ServiceWorker not available : Fallback to Window.setinterval');
      const intervalId = window.setInterval(callback, delay);
      if (options.immediateInvoke) {
        callback();
      }
      return () => window.clearInterval(intervalId);
    }

    const id = this.generateId() + (options?.callerName ? `-${options.callerName}` : '');

    // Store the wrapped callback for message handling
    this.pendingCallbacks.set(id, callback);

    // Execute immediately if requested
    if (options.immediateInvoke) {
      log.debug('Executing background setInterval for: ', id, `at: ${new Date().toString()} `);
      callback();
    }

    // Send start message to Service Worker
    this.sendMessage({
      type: 'ASYNC_INTERVAL',
      id: id,
      command: 'start',
      delay: delay
    });

    // Return cleanup function
    return (reason?: string) => {
      // Send stop message to Service Worker
      this.sendMessage({
        type: 'ASYNC_INTERVAL',
        id: id,
        command: 'stop'
      });

      // Remove the callback from pending callbacks immediately
      this.pendingCallbacks.delete(id);
      log.debug(`Cleanup for background interval : ID : ${id}`, reason ? `Reason: ${reason}` : '');
    };
  }

  /**
   * Creates a timeout that runs in the Service Worker
   * @param callback - Function to execute when timeout elapses
   * @param delay - Delay before execution in milliseconds
   * @param options - Configuration options
   * @returns Cleanup function to cancel the timeout
   */
  setAsyncTimeout(callback: MessageCallback, delay: number, options: AsyncOptions = {}): () => void {
    if (!this.serviceWorker) {
      // Fallback to window.setTimeout
      log.debug('ServiceWorker not available : Fallback to Window.setinterval');
      const timeoutlId = window.setTimeout(callback, delay);
      return () => window.clearTimeout(timeoutlId);
    }

    const id = this.generateId() + (options?.callerName ? `-${options.callerName}` : '');

    // Store the wrapped callback for message handling
    this.pendingCallbacks.set(id, callback);

    // Send start message to Service Worker
    this.sendMessage({
      type: 'ASYNC_TIMEOUT',
      id: id,
      command: 'start',
      delay: delay,
      context: options.context
    });

    // Return cleanup function
    return (reason?: string) => {
      // Remove the callback from pending callbacks immediately
      this.pendingCallbacks.delete(id);

      // Send stop message to Service Worker
      this.sendMessage({
        type: 'ASYNC_TIMEOUT',
        id: id,
        command: 'stop'
      });
      log.debug(`Cleanup for background timeout : ID : ${id}`, reason ? `Reason: ${reason}` : '');
    };
  }

  /**
   * Send heartbeat to keep the web service alive
   */
  sendHeartbeat() {
    this.sendMessage({
      type: 'HEARTBEAT',
      id: 'WFM_BackgroundService',
      command: 'beep',
      delay: this.HEARTBEAT_PULSE_DELAY
    });
  }
}
